/**
 * Redux actions, action creators, and redux-observable epics for the Timesheet domain.
 *
 * Covers CRUD operations on timesheet entries, series appointment lookups,
 * and CSV/file imports. Each operation follows the standard
 * `ACTION / ACTION_SUCCESS / ACTION_ERROR` triple with a matching epic that
 * handles the API call via RxJS ajax.
 *
 * @module TimesheetActions
 */

import {getInitialViewSettings, getViewSettingsSafely, ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {
    AllTimesheetTypes,
    AllTimesheetTypesProp,
    Timesheet,
    TimesheetEntryRequestProp,
    TimesheetModule,
    TimesheetRequest, TimesheetRestoreDeleteRequest
} from "../../models/timesheet/Timesheet";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import { ofType, StateObservable} from "redux-observable";
import { Observable, of } from 'rxjs';
import {ajax} from 'rxjs/ajax';
import {catchError, filter, map, mergeMap, switchMap} from 'rxjs/operators';
import {sendUserToPage, ViewActionTypes} from "../common/UserViewSettingsActions";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {
    getUrlParamsFromViewSettings,
    multipartHeaders,
    RouteConfig,
    RouteMethods,
    toFormDataEncode
} from "../../config/RouteConfig";
import {eventNotificationShow} from "../common/EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {createErrorResponse} from "../../config/languages/definitions/AjaxResponses";
import {currentLanguageFromStore} from "../../config/LanguageConfig";
import {Identifier} from "../../models/common/Identifier";
import {fetchMenuTimesheetHoursReport} from "../reporting/TimesheetReportActions";
import {getCurrentWeekRange} from "../../helpers/DateTimeHelpers";
import {fetchCalendarEntries} from "../calendar/CalendarActions";
import {DateRange} from "../../models/common/DateRange";


/** Union of all action types that the timesheet view reducer must handle. */
export type TimesheetViewActionTypes = FetchTimesheetEntriesActionTypes | ViewActionTypes  | ImportTimesheetEntryActionTypes

// ---------------------------------------------------------------------------
// FETCH TIMESHEET ENTRIES
// ---------------------------------------------------------------------------

/** Action dispatched to request a paginated / filtered list of timesheet entries. */
export interface FetchTimesheetEntriesAction extends ViewIdentifiable, ReduxAction<typeof FETCH_TIMESHEET_ENTRIES> {}

/** Action dispatched when the timesheet entries list has been fetched successfully. */
export interface FetchTimesheetEntriesSuccessAction extends ViewIdentifiable, ReduxAction< typeof FETCH_TIMESHEET_ENTRIES_SUCCESS > {
    /** Paginated API response containing the fetched timesheet entries. */
    payload: ApiReturnType< Timesheet[] >
}

/** Action dispatched when fetching timesheet entries fails. */
export interface FetchTimesheetEntriesErrorAction extends ViewIdentifiable, ErrorAction< typeof FETCH_TIMESHEET_ENTRIES_ERROR > {}

/** Discriminated union of all fetch-entries action shapes. */
export type FetchTimesheetEntriesActionTypes = FetchTimesheetEntriesErrorAction | FetchTimesheetEntriesSuccessAction | FetchTimesheetEntriesAction

export const FETCH_TIMESHEET_ENTRIES = "FETCH_TIMESHEET_ENTRIES";
export const FETCH_TIMESHEET_ENTRIES_SUCCESS = "FETCH_TIMESHEET_ENTRIES_SUCCESS";
export const FETCH_TIMESHEET_ENTRIES_ERROR = "FETCH_TIMESHEET_ENTRIES_ERROR";

/**
 * Creates an action to fetch timesheet entries for the given view.
 *
 * @param viewIdentifier - Unique identifier of the view requesting the data.
 * @returns A {@link FetchTimesheetEntriesAction} ready to be dispatched.
 */
export const fetchTimesheetEntries = ( viewIdentifier: string ): FetchTimesheetEntriesAction =>
    ({ type: FETCH_TIMESHEET_ENTRIES, viewIdentifier });

/**
 * Creates a success action carrying the fetched timesheet entries.
 *
 * @param payload - The paginated API response with timesheet entries.
 * @param viewIdentifier - Identifier of the originating view.
 * @returns A {@link FetchTimesheetEntriesSuccessAction}.
 */
export const fetchTimesheetEntriesSuccess = ( payload: ApiReturnType< Timesheet[] >, viewIdentifier: string ): FetchTimesheetEntriesSuccessAction =>
    ({ type: FETCH_TIMESHEET_ENTRIES_SUCCESS,  payload, viewIdentifier });

/**
 * Creates an error action for a failed timesheet entries fetch.
 *
 * @param error - The error thrown during the request.
 * @param viewIdentifier - Identifier of the originating view.
 * @returns A {@link FetchTimesheetEntriesErrorAction}.
 */
export const fetchTimesheetEntriesError = ( error: Error, viewIdentifier: string ): FetchTimesheetEntriesErrorAction =>
    ({ type: FETCH_TIMESHEET_ENTRIES_ERROR, error, viewIdentifier });

/**
 * Epic that listens for {@link FETCH_TIMESHEET_ENTRIES} actions scoped to the
 * main timesheet module, reads the current view settings (pagination, filters),
 * and performs a GET request to retrieve the matching entries.
 *
 * On success it dispatches {@link fetchTimesheetEntriesSuccess}; on failure it
 * dispatches both {@link fetchTimesheetEntriesError} and an error notification.
 */
export const fetchTimesheetEntriesEpic = (
    action$: Observable< FetchTimesheetEntriesAction >,
    store$: StateObservable<AppState>
) =>
    action$.pipe(
        ofType( FETCH_TIMESHEET_ENTRIES ),
        // Only handle actions from the main timesheet module; other views share this action type via ViewIdentifiable
        filter( action => action.viewIdentifier === TimesheetModule.identifier ),
        // mergeMap intentional: concurrent list fetches must not cancel each other (e.g. pagination + filter applied simultaneously)
        mergeMap( action => {
            const userViewSettings = getViewSettingsSafely( action.viewIdentifier, store$ );
            const path = `${ APP_CONFIG.baseWeb }timesheet/${  getUrlParamsFromViewSettings( userViewSettings) }`;
            const language = currentLanguageFromStore( store$ );

            return ajax( APP_CONFIG.getRequestObject(path) ).pipe(
                map( response => fetchTimesheetEntriesSuccess(
                    response.response as ApiReturnType< Timesheet[] >,
                    action.viewIdentifier
                ) ),
                catchError( error => of(
                    fetchTimesheetEntriesError(error, action.viewIdentifier ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, createErrorResponse( language, error.status, action.viewIdentifier, "fetchEntriesAction"))
                ) )
            )
        } )
    );


/** Callback signature invoked after a successful mutating operation. */
type ActionAfterModifyingSuccess = () => void


/**
 * Resolves the navigation target after a successful create / update.
 *
 * If the supplied {@link path} encodes a calendar route (comma-separated segments
 * containing "calendar"), the user is redirected to the calendar view anchored
 * at the 07:00 grid row. Otherwise the default timesheet list is used.
 *
 * @param path - Raw comma-separated path string from the action, or a simple route path.
 * @returns The resolved route path to navigate to.
 */
function createBackLinkPath( path: string ): string {

    let backLinkPath = RouteConfig.timesheet.routes.main.path;

    if( path && path.includes(",") ) {

        // Comma-separated path encodes multi-segment routes (e.g. "calendar,2024-01-15").
        // The anchor scrolls the calendar grid to the default working-day start (07:00).
        if( path.includes("calendar") ) {

            backLinkPath = RouteConfig.index.routes.main.path + path.split(",").join("/") + "/#grid-times-07:00:00";
        }
    }

    return backLinkPath;

}

// ---------------------------------------------------------------------------
// CREATE (INSERT) TIMESHEET ENTRY
// ---------------------------------------------------------------------------

/** Action dispatched to persist a new timesheet entry via POST. */
export interface CreateTimesheetEntryAction extends ReduxAction<typeof CREATE_TIMESHEET_ENTRY>, TimesheetEntryRequestProp {
    /** Optional route path to navigate to after a successful save. */
    path?: string
    /** When set, the calendar view is refreshed for this range instead of the default list refetch. */
    calCurrentDateRange?: DateRange
}

/** Action dispatched when a new timesheet entry has been created successfully. */
export interface CreateTimesheetEntrySuccessAction extends
    ReduxAction<typeof CREATE_TIMESHEET_ENTRY_SUCCESS >,
    AllTimesheetTypesProp {}

/** Action dispatched when creating a timesheet entry fails. */
export interface CreateTimesheetEntryErrorAction extends ErrorAction<typeof CREATE_TIMESHEET_ENTRY_ERROR>{}

/** Discriminated union of all create-entry action shapes. */
export type CreateTimesheetEntryActionTypes = CreateTimesheetEntryAction | CreateTimesheetEntryErrorAction | CreateTimesheetEntrySuccessAction

export const CREATE_TIMESHEET_ENTRY = "CREATE_TIMESHEET_ENTRY";
export const CREATE_TIMESHEET_ENTRY_SUCCESS = "CREATE_TIMESHEET_ENTRY_SUCCESS";
export const CREATE_TIMESHEET_ENTRY_ERROR = "CREATE_TIMESHEET_ENTRY_ERROR";

/**
 * Creates an action to persist a new timesheet entry.
 *
 * @param entry - The timesheet data to send to the API.
 * @param path - Optional navigation target after success (comma-separated calendar path supported).
 * @param calCurrentDateRange - When provided, triggers a calendar refetch instead of the default list refetch.
 * @returns A {@link CreateTimesheetEntryAction} ready to be dispatched.
 */
export const createTimesheetEntry = (
    entry: TimesheetRequest,
    path: string | undefined = undefined,
    calCurrentDateRange: DateRange | undefined = undefined
): CreateTimesheetEntryAction =>
    ({ type: CREATE_TIMESHEET_ENTRY, entry, path, calCurrentDateRange });

/**
 * Creates a success action after a timesheet entry has been persisted.
 *
 * @param entry - The newly created entry returned by the API.
 * @returns A {@link CreateTimesheetEntrySuccessAction}.
 */
export const createTimesheetEntrySuccess = ( entry: AllTimesheetTypes ): CreateTimesheetEntrySuccessAction => ({ type: CREATE_TIMESHEET_ENTRY_SUCCESS, entry });

/**
 * Creates an error action for a failed timesheet entry creation.
 *
 * @param error - The error thrown during the request.
 * @returns A {@link CreateTimesheetEntryErrorAction}.
 */
export const createTimesheetEntryError = ( error: Error ): CreateTimesheetEntryErrorAction => ({ type: CREATE_TIMESHEET_ENTRY_ERROR, error });

/**
 * Epic that listens for {@link CREATE_TIMESHEET_ENTRY}, sends a POST request
 * with the entry payload (converting Date objects to epoch milliseconds), and
 * on success dispatches the created entry, refreshes the hours report, shows a
 * success notification, refetches the list (or calendar), and navigates back.
 */
export const createTimesheetEntryEpic = ( action$: Observable< CreateTimesheetEntryAction >, store$: StateObservable< AppState > ) =>
    action$.pipe(
        ofType( CREATE_TIMESHEET_ENTRY ),
        switchMap( action => {
            const apiPath = `${APP_CONFIG.baseWeb}timesheet/`;

            const backLinkPath = createBackLinkPath( action.path || RouteConfig.timesheet.routes.main.path );


            return ajax( APP_CONFIG.getRequestObject(
                apiPath,
                RouteMethods.post, {
                    ...action.entry,
                    workPeriod: {
                        startAt: action.entry.workPeriod.startAt.getTime(),
                        finishAt:action.entry.workPeriod.finishAt.getTime()
                    },
                    seriesAppointment: action.entry.seriesAppointment === null
                        ? null
                        : {
                            ...action.entry.seriesAppointment,
                            finishAt: action.entry.seriesAppointment.finishAt.getTime()
                        }
                }
            ) ).pipe(
                mergeMap( response => of(
                    createTimesheetEntrySuccess( response.response as AllTimesheetTypes ),
                    fetchMenuTimesheetHoursReport(
                        [ { rawValue: action.entry.userId  }] ,
                        getCurrentWeekRange(),
                        getInitialViewSettings( TimesheetModule.identifier )
                    ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.ajaxSuccess ),
                    ( typeof action.calCurrentDateRange !== 'undefined'
                        ? fetchCalendarEntries(
                            TimesheetModule.identifier,
                            action.calCurrentDateRange
                        )
                        : fetchTimesheetEntries( TimesheetModule.identifier )
                    ),
                    sendUserToPage(
                        backLinkPath,
                        TimesheetModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createTimesheetEntryError(error),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, createErrorResponse(
                        currentLanguageFromStore(store$),
                        error.status,
                        TimesheetModule.identifier,
                        "createTimesheetEntry"
                    ))
                ))
            )
        })
    );



// ---------------------------------------------------------------------------
// UPDATE TIMESHEET ENTRY
// ---------------------------------------------------------------------------

/** Action dispatched to update an existing timesheet entry via PUT. */
export interface UpdateTimesheetEntryAction extends ReduxAction<typeof UPDATE_TIMESHEET_ENTRY>, TimesheetEntryRequestProp {
    /** Optional route path to navigate to after a successful save. */
    path?: string
    /** When set, the calendar view is refreshed for this range instead of the default list refetch. */
    calCurrentDateRange?: DateRange
}

/** Action dispatched when a timesheet entry has been updated successfully. */
export interface UpdateTimesheetEntrySuccessAction extends ReduxAction<typeof UPDATE_TIMESHEET_ENTRY_SUCCESS>, AllTimesheetTypesProp {}

/** Action dispatched when updating a timesheet entry fails. */
export interface UpdateTimesheetEntryErrorAction extends ErrorAction<typeof UPDATE_TIMESHEET_ENTRY_ERROR>{}

/** Discriminated union of all update-entry action shapes. */
export type UpdateTimesheetEntryActionTypes = UpdateTimesheetEntryAction
    | UpdateTimesheetEntrySuccessAction
    | UpdateTimesheetEntryErrorAction

export const UPDATE_TIMESHEET_ENTRY = "UPDATE_TIMESHEET_ENTRY";
export const UPDATE_TIMESHEET_ENTRY_SUCCESS = "UPDATE_TIMESHEET_ENTRY_SUCCESS";
export const UPDATE_TIMESHEET_ENTRY_ERROR = "UPDATE_TIMESHEET_ENTRY_ERROR";

/**
 * Creates an action to update an existing timesheet entry.
 *
 * @param entry - The updated timesheet data (must include `timesheetEntryId`).
 * @param path - Optional navigation target after success.
 * @param calCurrentDateRange - When provided, triggers a calendar refetch instead of the default list refetch.
 * @returns An {@link UpdateTimesheetEntryAction} ready to be dispatched.
 */
export const updateTimesheetEntry = (
    entry: TimesheetRequest,
    path: string | undefined = undefined,
    calCurrentDateRange: DateRange | undefined = undefined
): UpdateTimesheetEntryAction => ({ type: UPDATE_TIMESHEET_ENTRY, entry, path, calCurrentDateRange });

/**
 * Creates a success action after a timesheet entry has been updated.
 *
 * @param entry - The updated entry returned by the API.
 * @returns An {@link UpdateTimesheetEntrySuccessAction}.
 */
export const updateTimesheetEntrySuccess = ( entry: AllTimesheetTypes ): UpdateTimesheetEntrySuccessAction =>
    ({ type: UPDATE_TIMESHEET_ENTRY_SUCCESS, entry });

/**
 * Creates an error action for a failed timesheet entry update.
 *
 * @param error - The error thrown during the request.
 * @returns An {@link UpdateTimesheetEntryErrorAction}.
 */
export const updateTimesheetEntryError = ( error: Error ): UpdateTimesheetEntryErrorAction =>
    ({ type: UPDATE_TIMESHEET_ENTRY_ERROR, error });

/**
 * Epic that listens for {@link UPDATE_TIMESHEET_ENTRY}, sends a PUT request
 * with the updated payload, and on success dispatches the updated entry,
 * refreshes the hours report, shows a notification, navigates back, and
 * refetches the list or calendar.
 */
export const updateTimesheetEntryEpic = (
    action$: Observable< UpdateTimesheetEntryAction >,
    store$: StateObservable< AppState >
) => action$.pipe(
    ofType( UPDATE_TIMESHEET_ENTRY ),
    switchMap( action => {
        const path = `${APP_CONFIG.baseWeb}timesheet/${ action.entry.timesheetEntryId }`;
        const viewIdentifier = TimesheetModule.identifier;
        const backLinkPath = action.path || RouteConfig.timesheet.routes.main.path;

        return ajax( APP_CONFIG.getRequestObject( path, RouteMethods.put, {
                ...action.entry,
                workPeriod: {
                    startAt: action.entry.workPeriod.startAt.getTime(),
                    finishAt:action.entry.workPeriod.finishAt.getTime()
                }
            } )).pipe(
                mergeMap( response => of(
                    updateTimesheetEntrySuccess(response.response as AllTimesheetTypes ),
                    fetchMenuTimesheetHoursReport(
                        [ { rawValue: action.entry.userId } ],
                        getCurrentWeekRange(),
                        getInitialViewSettings( TimesheetModule.identifier )
                    ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.ajaxSuccess ),
                    sendUserToPage(
                        backLinkPath,
                        viewIdentifier
                    ),
                    ( typeof action.calCurrentDateRange !== 'undefined'
                            ? fetchCalendarEntries(
                                TimesheetModule.identifier,
                                action.calCurrentDateRange
                            )
                            : fetchTimesheetEntries( TimesheetModule.identifier )
                    ),
                )),
                catchError( error => of(
                    updateTimesheetEntryError(error),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, createErrorResponse(
                        currentLanguageFromStore(store$),
                        error.status,
                        viewIdentifier,
                        "updateTimesheetEntry"
                    ))
                ) )
            )
    })
);

// ---------------------------------------------------------------------------
// FETCH SINGLE TIMESHEET ENTRY
// ---------------------------------------------------------------------------

/** Action dispatched to fetch a single timesheet entry by its numeric id. */
export interface FetchTimesheetEntryAction extends ReduxAction<typeof FETCH_TIMESHEET_ENTRY >{
    /** The numeric id of the timesheet entry to retrieve. */
    id: number
}

/** Action dispatched when a single timesheet entry has been fetched successfully. */
export interface FetchTimesheetEntrySuccessAction extends ReduxAction<typeof FETCH_TIMESHEET_ENTRY_SUCCESS >, AllTimesheetTypesProp {

}

/** Action dispatched when fetching a single timesheet entry fails. */
export interface FetchTimesheetEntryErrorAction extends ErrorAction<typeof FETCH_TIMESHEET_ENTRY_ERROR >{}

/** Discriminated union of all fetch-single-entry action shapes. */
export type FetchTimesheetEntryActionTypes = FetchTimesheetEntryAction | FetchTimesheetEntryErrorAction | FetchTimesheetEntrySuccessAction

export const FETCH_TIMESHEET_ENTRY = "FETCH_TIMESHEET_ENTRY";
export const FETCH_TIMESHEET_ENTRY_SUCCESS = "FETCH_TIMESHEET_ENTRY_SUCCESS";
export const FETCH_TIMESHEET_ENTRY_ERROR = "FETCH_TIMESHEET_ENTRY_ERROR";

/**
 * Creates an action to fetch a single timesheet entry.
 *
 * @param id - Numeric identifier of the entry.
 * @returns A {@link FetchTimesheetEntryAction} ready to be dispatched.
 */
export const fetchTimesheetEntry = ( id: number ): FetchTimesheetEntryAction => ({ type: FETCH_TIMESHEET_ENTRY, id });

/**
 * Creates a success action carrying the fetched timesheet entry.
 *
 * @param entry - The entry returned by the API.
 * @returns A {@link FetchTimesheetEntrySuccessAction}.
 */
export const fetchTimesheetEntrySuccess = ( entry: AllTimesheetTypes ): FetchTimesheetEntrySuccessAction => ({ type: FETCH_TIMESHEET_ENTRY_SUCCESS, entry });

/**
 * Creates an error action for a failed single-entry fetch.
 *
 * @param error - The error thrown during the request.
 * @returns A {@link FetchTimesheetEntryErrorAction}.
 */
export const fetchTimesheetEntryError = ( error: Error ): FetchTimesheetEntryErrorAction => ({ type: FETCH_TIMESHEET_ENTRY_ERROR, error });

/**
 * Epic that listens for {@link FETCH_TIMESHEET_ENTRY}, performs a GET request
 * for the entry identified by `action.id`, and dispatches the result or an
 * error notification.
 */
export const fetchTimesheetEntryEpic = (
    action$: Observable< FetchTimesheetEntryAction >,
    store$: StateObservable<AppState>
) =>
    action$.pipe(
        ofType( FETCH_TIMESHEET_ENTRY ),
        switchMap( action => {

            const path = `${ APP_CONFIG.baseWeb }timesheet/${ action.id}`;

            const language = currentLanguageFromStore( store$ );

            return ajax( APP_CONFIG.getRequestObject(path) ).pipe(
                map( response => fetchTimesheetEntrySuccess( response.response as AllTimesheetTypes ) ),
                catchError( error => of(
                    fetchTimesheetEntryError(error ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, createErrorResponse( language, error.status, TimesheetModule.identifier, "fetchTimesheetEntryAction"))
                ) )
            )
        } )
    );

// ---------------------------------------------------------------------------
// RESTORE TIMESHEET ENTRY
// ---------------------------------------------------------------------------

/** Action dispatched to restore a previously soft-deleted timesheet entry via PATCH. */
export interface RestoreTimesheetEntryAction extends ReduxAction<typeof RESTORE_TIMESHEET_ENTRY>, AllTimesheetTypesProp {
    /** Payload describing the restore mutation to apply. */
    mutatingEntry: TimesheetRestoreDeleteRequest
}

/** Action dispatched when a timesheet entry has been restored successfully. */
export interface RestoreTimesheetEntrySuccessAction  extends ReduxAction<typeof RESTORE_TIMESHEET_ENTRY_SUCCESS>, AllTimesheetTypesProp {}

/** Action dispatched when restoring a timesheet entry fails. */
export interface RestoreTimesheetEntryErrorAction extends ErrorAction<typeof RESTORE_TIMESHEET_ENTRY_ERROR> {}

/** Discriminated union of all restore-entry action shapes. */
export type RestoreTimesheetEntryActionTypes = RestoreTimesheetEntryAction
    | RestoreTimesheetEntryErrorAction
    | RestoreTimesheetEntrySuccessAction

export const RESTORE_TIMESHEET_ENTRY = "RESTORE_TIMESHEET_ENTRY";
export const RESTORE_TIMESHEET_ENTRY_SUCCESS = "RESTORE_TIMESHEET_ENTRY_SUCCESS";
export const RESTORE_TIMESHEET_ENTRY_ERROR = "RESTORE_TIMESHEET_ENTRY_ERROR";

/**
 * Creates an action to restore a soft-deleted timesheet entry.
 *
 * @param entry - The full entry object to restore (used for its id and employee reference).
 * @param mutatingEntry - The restore-specific request payload.
 * @returns A {@link RestoreTimesheetEntryAction} ready to be dispatched.
 */
export function restoreTimesheetEntry(
    entry: AllTimesheetTypes,
    mutatingEntry: TimesheetRestoreDeleteRequest
): RestoreTimesheetEntryAction {
    return { type: RESTORE_TIMESHEET_ENTRY, entry, mutatingEntry };
}

/**
 * Creates a success action after a timesheet entry has been restored.
 *
 * @param entry - The restored entry returned by the API.
 * @returns A {@link RestoreTimesheetEntrySuccessAction}.
 */
export const restoreTimesheetEntrySuccess = ( entry: AllTimesheetTypes ): RestoreTimesheetEntrySuccessAction  =>
    ({ type: RESTORE_TIMESHEET_ENTRY_SUCCESS, entry });

/**
 * Creates an error action for a failed restore operation.
 *
 * @param error - The error thrown during the request.
 * @returns A {@link RestoreTimesheetEntryErrorAction}.
 */
export const restoreTimesheetEntryError = ( error: Error ): RestoreTimesheetEntryErrorAction  =>
    ({ type: RESTORE_TIMESHEET_ENTRY_ERROR, error });

/**
 * Epic that listens for {@link RESTORE_TIMESHEET_ENTRY}, sends a PATCH request
 * to restore the entry, and on success refreshes the hours report, refetches
 * the list, shows a notification, and navigates back to the timesheet list.
 */
export const restoreTimesheetEntryEpic = (
    action$: Observable< RestoreTimesheetEntryAction >,
    store$: StateObservable< AppState >
) => action$.pipe(
    ofType( RESTORE_TIMESHEET_ENTRY ),
    switchMap( action => {
        const path = `${APP_CONFIG.baseWeb}timesheet/${ action.entry.timesheetEntryId.rawValue }`;
        const viewIdentifier = TimesheetModule.identifier;

        return ajax(
            APP_CONFIG.getRequestObject(
                path,
                RouteMethods.patch ,
                action.mutatingEntry
            )
        ).pipe(
            mergeMap( response => of(
                restoreTimesheetEntrySuccess( response.response as AllTimesheetTypes ),
                fetchMenuTimesheetHoursReport(
                    [ { rawValue: action.entry.employee.userId.rawValue } ],
                    getCurrentWeekRange(),
                    getInitialViewSettings( TimesheetModule.identifier )
                ),
                fetchTimesheetEntries( TimesheetModule.identifier ),
                eventNotificationShow( EVENT_NOTIFICATION_TYPE.ajaxSuccess ),
                sendUserToPage( RouteConfig.timesheet.routes.main.path, TimesheetModule.identifier )
            )),
            catchError( error => of(
                restoreTimesheetEntryError(error),
                eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, createErrorResponse(
                    currentLanguageFromStore(store$),
                    error.status,
                    viewIdentifier,
                    "restoreTimesheetEntry"
                ))
            ) )
        )
    })
);

// ---------------------------------------------------------------------------
// DELETE TIMESHEET ENTRY
// ---------------------------------------------------------------------------

/** Action dispatched to soft-delete a timesheet entry via DELETE. */
export interface DeleteTimesheetEntryAction extends ReduxAction<typeof DELETE_TIMESHEET_ENTRY>, AllTimesheetTypesProp {
    /** Payload describing the delete mutation to apply. */
    mutatingEntry: TimesheetRestoreDeleteRequest
}

/** Action dispatched when a timesheet entry has been deleted successfully. */
export interface DeleteTimesheetEntrySuccessAction  extends ReduxAction<typeof DELETE_TIMESHEET_ENTRY_SUCCESS>, AllTimesheetTypesProp {}

/** Action dispatched when deleting a timesheet entry fails. */
export interface DeleteTimesheetEntryErrorAction extends ErrorAction<typeof DELETE_TIMESHEET_ENTRY_ERROR> {}

/** Discriminated union of all delete-entry action shapes. */
export type DeleteTimesheetEntryActionTypes = DeleteTimesheetEntryAction
    | DeleteTimesheetEntryErrorAction
    | DeleteTimesheetEntrySuccessAction

export const DELETE_TIMESHEET_ENTRY = "DELETE_TIMESHEET_ENTRY";
export const DELETE_TIMESHEET_ENTRY_SUCCESS = "DELETE_TIMESHEET_ENTRY_SUCCESS";
export const DELETE_TIMESHEET_ENTRY_ERROR = "DELETE_TIMESHEET_ENTRY_ERROR";

/**
 * Creates an action to soft-delete a timesheet entry.
 *
 * @param entry - The full entry object to delete (used for its id and employee reference).
 * @param mutatingEntry - The delete-specific request payload.
 * @returns A {@link DeleteTimesheetEntryAction} ready to be dispatched.
 */
export function deleteTimesheetEntry(
    entry: AllTimesheetTypes ,
    mutatingEntry: TimesheetRestoreDeleteRequest
): DeleteTimesheetEntryAction {
    return { type: DELETE_TIMESHEET_ENTRY, entry, mutatingEntry };
}

/**
 * Creates a success action after a timesheet entry has been deleted.
 *
 * @param entry - The deleted entry returned by the API.
 * @returns A {@link DeleteTimesheetEntrySuccessAction}.
 */
export const deleteTimesheetEntrySuccess = ( entry: AllTimesheetTypes ): DeleteTimesheetEntrySuccessAction  =>
    ({ type: DELETE_TIMESHEET_ENTRY_SUCCESS, entry });

/**
 * Creates an error action for a failed delete operation.
 *
 * @param error - The error thrown during the request.
 * @returns A {@link DeleteTimesheetEntryErrorAction}.
 */
export const deleteTimesheetEntryError = ( error: Error ): DeleteTimesheetEntryErrorAction  =>
    ({ type: DELETE_TIMESHEET_ENTRY_ERROR, error });

/**
 * Epic that listens for {@link DELETE_TIMESHEET_ENTRY}, sends a DELETE request,
 * and on success refreshes the hours report, refetches the list, shows a
 * notification, and navigates back to the timesheet list.
 */
export const deleteTimesheetEntryEpic = (
    action$: Observable< DeleteTimesheetEntryAction >,
    store$: StateObservable< AppState >
) => action$.pipe(
    ofType( DELETE_TIMESHEET_ENTRY ),
    switchMap( action => {
        const path = `${APP_CONFIG.baseWeb}timesheet/${ action.entry.timesheetEntryId.rawValue }`;
        const viewIdentifier = TimesheetModule.identifier;

        return ajax(
            APP_CONFIG.getRequestObject(
                path,
                RouteMethods.delete,
                action.mutatingEntry
            )
        ).pipe(
            mergeMap( response => of(
                deleteTimesheetEntrySuccess( response.response as AllTimesheetTypes ),
                fetchMenuTimesheetHoursReport(
                    [ { rawValue: action.entry.employee.userId.rawValue } ],
                    getCurrentWeekRange(),
                    getInitialViewSettings( TimesheetModule.identifier )
                ),
                fetchTimesheetEntries( TimesheetModule.identifier ),
                eventNotificationShow( EVENT_NOTIFICATION_TYPE.ajaxSuccess ),
                sendUserToPage( RouteConfig.timesheet.routes.main.path, TimesheetModule.identifier )
            )),
            catchError( error => of(
                deleteTimesheetEntryError(error),
                eventNotificationShow( EVENT_NOTIFICATION_TYPE.customAjaxError, createErrorResponse(
                    currentLanguageFromStore(store$),
                    error.status,
                    viewIdentifier,
                    "deleteTimesheetEntry"
                ))
            ) )
        )
    })
);

// ---------------------------------------------------------------------------
// FETCH SERIES TIMESHEET APPOINTMENT
// ---------------------------------------------------------------------------

/** Action dispatched to fetch all entries belonging to a recurring series. */
export interface FetchSeriesAppointmentsAction extends ReduxAction< typeof FETCH_SERIES_APPOINTMENT>  {
    /** Identifier of the recurring series to look up. */
    seriesId: Identifier<number>
    /** Discriminator for the type of timesheet entry (`'internal'` or `'project'`). */
    typeIdentifier: 'internal' | 'project'
}

/** Action dispatched when the series entries have been fetched successfully. */
export interface FetchSeriesAppointmentsSuccessAction extends ReduxAction<typeof FETCH_SERIES_APPOINTMENT_SUCCESS> {
    /** Paginated API response containing the series entries. */
    payload: ApiReturnType< AllTimesheetTypes[] >
    /** Identifier of the series these entries belong to. */
    seriesId: Identifier<number>
}

/** Action dispatched when fetching series entries fails. */
export interface FetchSeriesAppointmentsErrorAction extends ErrorAction<typeof FETCH_SERIES_APPOINTMENT_ERROR > {}

/** Discriminated union of all fetch-series action shapes. */
export type FetchSeriesAppointmentsActionTypes = FetchSeriesAppointmentsAction | FetchSeriesAppointmentsErrorAction | FetchSeriesAppointmentsSuccessAction

export const FETCH_SERIES_APPOINTMENT = "FETCH_SERIES_APPOINTMENT"
export const FETCH_SERIES_APPOINTMENT_SUCCESS = "FETCH_SERIES_APPOINTMENT_SUCCESS"
export const FETCH_SERIES_APPOINTMENT_ERROR = "FETCH_SERIES_APPOINTMENT_ERROR"

/**
 * Creates an action to fetch all entries of a recurring series.
 *
 * @param seriesId - Identifier of the series.
 * @param typeIdentifier - Whether the series is `'internal'` or `'project'`-based.
 * @returns A {@link FetchSeriesAppointmentsAction} ready to be dispatched.
 */
export const fetchSeriesAppointments = ( seriesId: Identifier<number>, typeIdentifier: 'internal' | 'project' ): FetchSeriesAppointmentsAction =>
    ({ type: FETCH_SERIES_APPOINTMENT, seriesId, typeIdentifier });

/**
 * Creates a success action carrying the fetched series entries.
 *
 * @param payload - The paginated API response with series entries.
 * @param seriesId - Identifier of the series.
 * @returns A {@link FetchSeriesAppointmentsSuccessAction}.
 */
export const fetchSeriesAppointmentsSuccess = ( payload: ApiReturnType< AllTimesheetTypes[] >, seriesId: Identifier<number> ): FetchSeriesAppointmentsSuccessAction =>
    ({ type: FETCH_SERIES_APPOINTMENT_SUCCESS, payload, seriesId });

/**
 * Creates an error action for a failed series fetch.
 *
 * @param error - The error thrown during the request.
 * @returns A {@link FetchSeriesAppointmentsErrorAction}.
 */
export const fetchSeriesAppointmentsError = ( error: Error ): FetchSeriesAppointmentsErrorAction =>
    ({ type: FETCH_SERIES_APPOINTMENT_ERROR, error });

/**
 * Epic that listens for {@link FETCH_SERIES_APPOINTMENT}, performs a GET
 * request for the series identified by `seriesId` and `typeIdentifier`, and
 * dispatches the result or an error notification.
 */
export const fetchSeriesAppointmentsEpic = ( action$:Observable< FetchSeriesAppointmentsAction >, store$: StateObservable< AppState > ) =>
    action$.pipe(
        ofType( FETCH_SERIES_APPOINTMENT ),
        switchMap( action => {

            const path = `${ APP_CONFIG.baseWeb }timesheet/series/${ action.seriesId.rawValue}/${ action.typeIdentifier }`;

            const language = currentLanguageFromStore( store$ );

            return ajax( APP_CONFIG.getRequestObject(path) ).pipe(
                map( response => fetchSeriesAppointmentsSuccess(
                    response.response as ApiReturnType< AllTimesheetTypes[] >,
                    action.seriesId
                ) ),
                catchError( error => of(
                    fetchSeriesAppointmentsError(error ),
                    eventNotificationShow(
                        EVENT_NOTIFICATION_TYPE.customAjaxError,
                        createErrorResponse( language, error.status, TimesheetModule.identifier, action.type)
                    )
                ) )
            )
        } )
    );

// ---------------------------------------------------------------------------
// IMPORT TIMESHEET ENTRIES
// ---------------------------------------------------------------------------

/** Action dispatched to import timesheet entries from uploaded files via multipart POST. */
export interface ImportTimesheetEntryAction extends ReduxAction<typeof IMPORT_TIMESHEET_ENTRY> {
    /** The file(s) selected by the user for import. */
    files: File[]
}

/** Action dispatched when the import has completed successfully. */
export interface ImportTimesheetEntrySuccessAction extends
    ReduxAction<typeof IMPORT_TIMESHEET_ENTRY_SUCCESS > {}

/** Action dispatched when the import fails. */
export interface ImportTimesheetEntryErrorAction extends ErrorAction<typeof IMPORT_TIMESHEET_ENTRY_ERROR>{}

/** Discriminated union of all import action shapes. */
export type ImportTimesheetEntryActionTypes = ImportTimesheetEntryAction | ImportTimesheetEntryErrorAction | ImportTimesheetEntrySuccessAction

export const IMPORT_TIMESHEET_ENTRY = "IMPORT_TIMESHEET_ENTRY";
export const IMPORT_TIMESHEET_ENTRY_SUCCESS = "IMPORT_TIMESHEET_ENTRY_SUCCESS";
export const IMPORT_TIMESHEET_ENTRY_ERROR = "IMPORT_TIMESHEET_ENTRY_ERROR";

/**
 * Creates an action to import timesheet entries from one or more files.
 *
 * @param files - Array of `File` objects to upload.
 * @returns An {@link ImportTimesheetEntryAction} ready to be dispatched.
 */
export const importTimesheetEntry = (files: File[] ): ImportTimesheetEntryAction =>
    ({ type: IMPORT_TIMESHEET_ENTRY, files });

/**
 * Creates a success action after the import has completed.
 *
 * @returns An {@link ImportTimesheetEntrySuccessAction}.
 */
export const importTimesheetEntrySuccess = ( ): ImportTimesheetEntrySuccessAction => ({ type: IMPORT_TIMESHEET_ENTRY_SUCCESS });

/**
 * Creates an error action for a failed import.
 *
 * @param error - The error thrown during the request.
 * @returns An {@link ImportTimesheetEntryErrorAction}.
 */
export const importTimesheetEntryError = ( error: Error ): ImportTimesheetEntryErrorAction => ({ type: IMPORT_TIMESHEET_ENTRY_ERROR, error });

/**
 * Epic that listens for {@link IMPORT_TIMESHEET_ENTRY}, uploads the files as
 * multipart form data, and on success refreshes the hours report and the
 * timesheet list while showing a success notification.
 */
export const importTimesheetEntryEpic = ( action$: Observable< ImportTimesheetEntryAction >, store$: StateObservable< AppState > ) =>
    action$.pipe(
        ofType( IMPORT_TIMESHEET_ENTRY ),
        switchMap( action => {
            const path = `${APP_CONFIG.baseWeb}timesheet/import`;

            console.log( "test" )

            return ajax( APP_CONFIG.getRequestObject(
                path,
                RouteMethods.post,
                toFormDataEncode( 'import', {}, action.files ),
                multipartHeaders()
            ) ).pipe(
                mergeMap( response => of(
                    importTimesheetEntrySuccess(),
                    fetchMenuTimesheetHoursReport(
                        [ { rawValue: store$.value.AuthenticatedUserReducer.current?.id?.rawValue ?? 0  }] ,
                        getCurrentWeekRange(),
                        getInitialViewSettings( TimesheetModule.identifier )
                    ),
                    fetchTimesheetEntries( TimesheetModule.identifier ),
                    eventNotificationShow( EVENT_NOTIFICATION_TYPE.ajaxSuccess )
                ) ),
                catchError( error => of(
                    importTimesheetEntryError(error),
                    eventNotificationShow(
                        EVENT_NOTIFICATION_TYPE.customAjaxError,
                        createErrorResponse(
                            currentLanguageFromStore(store$),
                            error.status,
                            TimesheetModule.identifier,
                            "importTimesheetEntry"
                        )
                    )
                ))
            )
        })
    );
